import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { publicRoutes } from '../../routes/routes';
import { notificationAPI } from '../../service/api';
import { resolveUploadUrl } from '../../config/config';
import { FiMenu, FiX, FiBell, FiUser, FiLogOut, FiGrid, FiChevronDown } from 'react-icons/fi';
import useAutoRefresh from '../../hooks/useAutoRefresh';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const menuRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const res = await notificationAPI.getUnreadCount();
      setUnreadCount(res.data.data.unread);
    } catch {}
  }, [user]);

  useEffect(() => { fetchUnreadCount(); }, [fetchUnreadCount]);
  useAutoRefresh(() => fetchUnreadCount(), 30000);

  useEffect(() => {
    if (!socket || !user) return;

    const incrementCount = () => setUnreadCount((prev) => prev + 1);

    socket.on('new_message', incrementCount);
    socket.on('new_comment', incrementCount);
    socket.on('status_update', incrementCount);

    return () => {
      socket.off('new_message', incrementCount);
      socket.off('new_comment', incrementCount);
      socket.off('status_update', incrementCount);
    };
  }, [socket, user]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setUserMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const roleLabels = {
    student: 'Étudiant',
    owner: 'Propriétaire',
    admin: 'Administrateur',
    service_acad: 'Service Académique',
  };

  return (
    <nav style={{ backgroundColor: '#0A192F', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      <div className="page-container">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="md:hidden flex items-center gap-2 flex-shrink-0">
            <span className="font-serif text-2xl font-bold text-white tracking-tight">
              UNI<span style={{ color: '#C59B6C' }}>KOL</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8 flex-1">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <span className="font-serif text-2xl font-bold text-white tracking-tight">
                UNI<span style={{ color: '#C59B6C' }}>KOL</span>
              </span>
            </Link>
            <div className="flex items-center gap-6">
              {publicRoutes.map((route) => (
                <Link
                  key={route.path}
                  to={route.path}
                  className="text-sm font-medium transition-colors"
                  style={{ color: '#88929B' }}
                  onMouseEnter={(e) => e.target.style.color = '#FFFFFF'}
                  onMouseLeave={(e) => e.target.style.color = '#88929B'}
                >
                  {route.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <Link to="/notifications" className="relative transition-colors" style={{ color: '#88929B' }}
                  onMouseEnter={(e) => e.target.style.color = '#FFFFFF'}
                  onMouseLeave={(e) => e.target.style.color = '#88929B'}>
                  <FiBell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 flex items-center justify-center rounded-full text-[9px] font-bold px-1"
                      style={{ backgroundColor: '#F4F4F0', color: '#0A192F' }}>
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>
                <div className="relative" ref={menuRef}>
                  <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 px-4 py-2 transition-colors"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: '#FFFFFF', borderRadius: 0 }}>
                    {user.avatarUrl ? (
                      <img src={resolveUploadUrl(user.avatarUrl)} alt="" className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <FiGrid className="w-4 h-4" />
                    )}
                    <span className="hidden lg:inline text-sm font-medium">{user.nom}</span>
                    <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5"
                      style={{ backgroundColor: '#C59B6C', color: '#0A192F' }}>
                      {roleLabels[user.role] || user.role}
                    </span>
                    <FiChevronDown className="w-3 h-3" style={{ color: '#88929B' }} />
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 shadow-lg"
                      style={{ backgroundColor: '#FFFFFF', borderRadius: 0, zIndex: 50 }}>
                      <div className="px-4 py-3 border-b" style={{ borderColor: '#E5E5E0' }}>
                        <p className="text-sm font-bold" style={{ color: '#0A192F' }}>{user.nom}</p>
                        <p className="text-xs mt-0.5" style={{ color: '#7A828A' }}>{user.email}</p>
                      </div>
                      <Link to={`/dashboard/${user.role}`}
                        className="flex items-center gap-3 px-4 py-3 text-sm transition-colors"
                        style={{ color: '#0A192F' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#F4F4F0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        onClick={() => setUserMenuOpen(false)}>
                        <FiGrid className="w-4 h-4" style={{ color: '#C59B6C' }} />
                        Mon tableau de bord
                      </Link>
                      <Link to="/profil"
                        className="flex items-center gap-3 px-4 py-3 text-sm transition-colors"
                        style={{ color: '#0A192F' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#F4F4F0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        onClick={() => setUserMenuOpen(false)}>
                        <FiUser className="w-4 h-4" style={{ color: '#C59B6C' }} />
                        Mon profil
                      </Link>
                      <div className="border-t" style={{ borderColor: '#E5E5E0' }}>
                        <button onClick={handleLogout}
                          className="flex items-center gap-3 w-full px-4 py-3 text-sm transition-colors"
                          style={{ color: '#FF6B6B' }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#FFF5F5'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                          <FiLogOut className="w-4 h-4" />
                          Déconnexion
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="px-5 py-2 text-sm font-bold tracking-wider uppercase border-2 transition-colors"
                  style={{ color: '#FFFFFF', borderColor: '#FFFFFF', borderRadius: 0 }}>
                  Connexion
                </Link>
                <Link to="/register" className="px-5 py-2 text-sm font-bold tracking-wider uppercase transition-colors"
                  style={{ backgroundColor: '#C59B6C', color: '#0A192F', borderRadius: 0 }}>
                  Inscription
                </Link>
              </>
            )}
          </div>

          <div className="md:hidden flex items-center gap-3">
            {user && (
              <Link to="/notifications" className="relative transition-colors" style={{ color: '#88929B' }}>
                <FiBell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 flex items-center justify-center rounded-full text-[9px] font-bold px-1"
                    style={{ backgroundColor: '#F4F4F0', color: '#0A192F' }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>
            )}
            <button style={{ color: '#FFFFFF' }} onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden py-4 space-y-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            {publicRoutes.map((route) => (
              <Link key={route.path} to={route.path} className="block text-sm" style={{ color: '#88929B' }}
                onClick={() => setMobileOpen(false)}>
                {route.label}
              </Link>
            ))}
            <hr style={{ borderColor: 'rgba(255,255,255,0.08)' }} />
            {user ? (
              <>
                <Link to={`/dashboard/${user.role}`} className="block text-sm text-white" onClick={() => setMobileOpen(false)}>
                  Mon tableau de bord
                </Link>
                <Link to="/profil" className="block text-sm" style={{ color: '#88929B' }} onClick={() => setMobileOpen(false)}>
                  Mon profil
                </Link>
                <button onClick={handleLogout} className="block text-sm" style={{ color: '#FF6B6B' }}>
                  Déconnexion
                </button>
              </>
            ) : (
              <div className="flex gap-3">
                <Link to="/login" className="btn-outline-root btn-sm flex-1 text-center">Connexion</Link>
                <Link to="/register" className="btn-primary-root btn-sm flex-1 text-center">Inscription</Link>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
